import { compileDefinitionInputs, definitionReaders, type DefinitionRow, type DefinitionReaders } from './location-type-ambience';
import { searchCandidate, searchDefinitionRow } from './search-definition-results';
import { fromBaseRecord } from '../compile/from-base-context';
import { phpJsonFloat } from '../compile/php-decimal-values';
import { phpJsonTruthy } from '../compile/php-json-values';
import { layerListEntryUuid } from '../../shared/array-types';
import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import { strnatcasecmp } from '../php-compat/strings';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';

export const CREATURE_FILTER_LAYERS = ['creature_type', 'creature_sub_type', 'creature_template', 'size',
    'damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability', 'status_effect_permanent',
    'creature_environment', 'lair', 'regional_effect'] as const;
export type CreatureFilterSelection = Record<string, unknown>;
export const CREATURE_FILTER_CAP = 100;

const values = (value: unknown): unknown[] => value == null || value === '' ? [] : Array.isArray(value) ? value : [value];
const AUTHORED_FILTERS = ['creature_type', 'creature_sub_type', 'creature_template', 'creature_environment',
    'lair', 'regional_effect', 'status_effect_permanent'] as const;

/** Follow saved layer references, including wrapped/scored entries, without interpreting their effects. */
function definitionReferences(value: unknown, definitions: DefinitionRow): string[] {
    if (typeof value === 'string') return value.split('|').map(part => readItemUuidSlot(part).toUpperCase())
        .filter(id => id && fromBaseRecord(definitions[id]));
    return value && typeof value === 'object' ? Object.values(value).flatMap(child => definitionReferences(child, definitions)) : [];
}

function definitionClosure(id: string, definitions: DefinitionRow, includeCreatures: boolean, sharedIds: readonly string[] = []): DefinitionRow[] {
    const seen = new Set<string>(), pending = [id, ...sharedIds], result: DefinitionRow[] = [];
    while (pending.length) {
        const next = pending.pop()!;
        if (seen.has(next)) continue;
        seen.add(next);
        const node = definitions[next];
        if (!fromBaseRecord(node) || next !== id && !includeCreatures && definitionReaders.item(node, 'item_type') === 'creature') continue;
        result.push(node);
        pending.push(...definitionReferences(node.item_layer_types, definitions));
    }
    return result;
}

/** Authored values are a superset: grants may be conditional, and another creature's traits never belong to this one. */
function mayMatchCreatureFilters(definition: DefinitionRow, closure: DefinitionRow[], selection: CreatureFilterSelection): boolean {
    for (const key of ['cr_range_begin', 'cr_range_end']) {
        if (!values(selection[key]).length) continue;
        const cr = definitionReaders.layer(definition, 'cr');
        if (cr != null && (key === 'cr_range_begin' ? phpJsonFloat(cr) < phpJsonFloat(selection[key]) : phpJsonFloat(cr) > phpJsonFloat(selection[key]))) return false;
    }
    return AUTHORED_FILTERS.every(key => {
        const wanted = values(selection[key]);
        return !wanted.length || closure.some(node => {
            const held = creatureFilterValues(node, key);
            return wanted.some(value => held.includes(String(value)));
        });
    });
}

/** The page's aggregated layer reader includes providers, wrapped permanent statuses and computed defenses. */
export function creatureFilterValues(host: DefinitionRow, key: string): string[] {
    return window.ftLayerEntries(host, key).map((entry: unknown) => key.startsWith('damage_type_') && fromBaseRecord(entry)
        && typeof entry.damage_type === 'string' ? entry.damage_type : layerListEntryUuid(entry)).filter(Boolean);
}

/** AND across admitted filters, OR within each multiselect. An absent numeric layer cannot exclude a row. */
export function matchesCreatureFilters(host: DefinitionRow, selection: CreatureFilterSelection, read: DefinitionReaders = definitionReaders): boolean {
    for (const [key, minimum] of [['cr_range_begin', true], ['cr_range_end', false]] as const) {
        if (!values(selection[key]).length) continue;
        const cr = read.layer(host, 'cr');
        if (cr != null && (minimum ? phpJsonFloat(cr) < phpJsonFloat(selection[key]) : phpJsonFloat(cr) > phpJsonFloat(selection[key]))) return false;
    }
    return CREATURE_FILTER_LAYERS.every(key => {
        const wanted = values(selection[key]);
        if (!wanted.length) return true;
        const held = creatureFilterValues(host, key);
        return wanted.some(value => held.includes(String(value)));
    });
}

/** Narrow saved fields first; only survivors need the page owner's computed filters and presentation. */
export async function creatureFilterResults(value: unknown, selection: CreatureFilterSelection, current: () => boolean = () => true) {
    if (!fromBaseRecord(value) || value.shape_v !== 2 || value.renderer !== 'encounter_creature_picker' || !Array.isArray(value.results)
        || !fromBaseRecord(value.viewer) || !fromBaseRecord(value.sources) || !fromBaseRecord(value.definitions)
        || !fromBaseRecord(value.inputs) || typeof value.root !== 'string')
        throw new Error('Filter inputs unavailable. Please retry.');
    const viewer = value.viewer, sources = value.sources, inputs = value.inputs;
    const definitions = { ...fromBaseRecord(inputs.catalog) ? inputs.catalog : {}, ...value.definitions };
    const sourceSelection = values(selection.sources).filter(source => source !== '' && source != null);
    const selectedSources = sourceSelection.length ? sourceSelection : values(selection.source_id);
    const keyword = typeof selection.keyword === 'string' ? selection.keyword.trim().toLowerCase() : '';
    const eligible = value.results.filter(fromBaseRecord).filter(row => {
        const state = searchCandidate(row, viewer, sources, true);
        const field = (key: string) => definitionReaders.item({ items: row }, key);
        return state.type === 'creature' && state.visible && !phpJsonTruthy(field('homebrew'))
            && (!phpJsonTruthy(selection.only_owned) || state.enabled)
            && (!selectedSources.length || selectedSources.includes(state.sourceId))
            && (!keyword || decodeHtmlEntitiesForPlainText(field('item_name')).toLowerCase().includes(keyword));
    });
    const candidates = eligible.filter(row => {
        const id = String(definitionReaders.item({ items: row }, 'item_id')).toUpperCase(), definition = definitions[id];
        // A missing definition is reported by the normal compile path, rather than silently lost in narrowing.
        return !fromBaseRecord(definition) || mayMatchCreatureFilters(definition, definitionClosure(id, definitions, false), selection);
    });
    const results: ReturnType<typeof searchDefinitionRow>[] = [];
    const unchecked: { label: string; reason: string }[] = [];
    const compiled = new Map<string, DefinitionRow | null>();
    // Template members and table outcomes are saved in sidecars rather than definition layer buckets.
    const sharedIds = ['enchantment_templates', 'enchantment_template_enchantments', 'enchantment_match_rows',
        'monster_templates', 'monster_template_layers', 'random_table_rows'].flatMap(key => definitionReferences(inputs[key], definitions));
    for (const row of candidates) {
        if (!current()) return null;
        const id = String(definitionReaders.item({ items: row }, 'item_id')).toUpperCase();
        try {
            if (!compiled.has(id)) {
                // Keep the run's shared sidecars/metadata; transfer only this candidate's reference closure to the owner.
                const catalog = Object.fromEntries(definitionClosure(id, definitions, true, sharedIds).map(node => [
                    String(definitionReaders.item(node, 'item_id')).toUpperCase(), node]));
                compiled.set(id, null);
                compiled.set(id, (await compileDefinitionInputs({ ...value, inputs: { ...inputs, catalog } }, [id])).get(id) ?? null);
            }
            if (!current()) return null;
            const host = compiled.get(id);
            if (!host) throw new Error('Filter definition unavailable. Please retry.');
            if (matchesCreatureFilters(host, selection)) results.push(searchDefinitionRow(row, host, viewer, sources, true, value.root));
        } catch (error) {
            if (!current()) return null;
            unchecked.push({ label: decodeHtmlEntitiesForPlainText(definitionReaders.item({ items: row }, 'item_name')),
                reason: error instanceof Error ? error.message : 'Definition compilation failed.' });
        }
    }
    results.sort((a, b) => strnatcasecmp(a.label, b.label));
    return { shape_v: 1, renderer: value.renderer, results: results.slice(0, CREATURE_FILTER_CAP), match_count: results.length,
        candidate_count: candidates.length, compile_count: compiled.size, unchecked };
}
